import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  addStop,
  attachOrder,
  deleteStop,
  deleteTrip,
  detachOrder,
  driverSuggest,
  getTrip,
  getTripRoute,
  listCarriers,
  reorderStops,
  updateStop,
  updateTrip,
  type DriverSuggestion,
  type TripDetail,
  type TripStatus,
  type TripUpdateIn,
} from "@/api/logistics";
import { RemotePicker } from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage } from "@/lib/api-errors";
import { ruRub } from "@/lib/format";
import { StopCard } from "@/pages/logistics/StopCard";
import { TripMap } from "@/pages/logistics/TripMap";

const STATUS_OPTIONS: { value: TripStatus; label: string }[] = [
  { value: "planned", label: "В планах" },
  { value: "completed", label: "Завершён" },
  { value: "cancelled", label: "Отменён" },
];

const VEHICLE_TYPES = [
  "Легковая",
  "Каблук",
  "ГАЗель",
  "3-тонник",
  "5-тонник",
  "10-тонник",
  "Фура",
];

export function TripPage(): React.ReactElement {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mapOpen, setMapOpen] = useState(false);

  const tripQ = useQuery({
    queryKey: ["trip", id],
    queryFn: () => getTrip(id),
    enabled: !!id,
  });

  const apply = (d: TripDetail) => qc.setQueryData(["trip", id], d);

  const act = async (fn: () => Promise<TripDetail>, errMsg: string): Promise<boolean> => {
    try {
      apply(await fn());
      return true;
    } catch (e) {
      toast.error(extractErrorMessage(e, errMsg));
      return false;
    }
  };

  // Дистанции плеч (OSRM) — для коннекторов «доставка до точки N». Показываем
  // только когда у ВСЕХ точек есть координаты (тогда плечи совпадают с точками).
  const routeStops = tripQ.data?.stops ?? [];
  const allHaveCoords = routeStops.length >= 2 && routeStops.every((s) => s.lat && s.lon);
  const routeQ = useQuery({
    queryKey: ["trip-route", id, JSON.stringify(routeStops.map((s) => [s.lat, s.lon]))],
    queryFn: () => getTripRoute(id),
    enabled: allHaveCoords,
    retry: false,
    staleTime: 60_000,
  });
  const legs =
    routeQ.data?.ok && routeQ.data.legs_km.length === routeStops.length - 1
      ? routeQ.data.legs_km
      : null;

  if (tripQ.isLoading) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }
  if (tripQ.isError || !tripQ.data) {
    // 404 — рейс действительно удалён/не существует. Любая другая ошибка
    // (500/сеть/403) — НЕ «не найден»: показываем настоящую причину, иначе
    // маскируем баг сервера под «рейса нет».
    const httpStatus = (tripQ.error as { response?: { status?: number } })?.response
      ?.status;
    const notFound = httpStatus === 404;
    return (
      <div className="mx-auto max-w-5xl space-y-3">
        <BackLink onClick={() => navigate("/logistics")} />
        <Card className="border-destructive/50 p-3 text-sm text-destructive">
          {notFound
            ? "Рейс не найден."
            : `Не удалось загрузить рейс: ${extractErrorMessage(
                tripQ.error,
                "ошибка сервера",
              )}`}
        </Card>
      </div>
    );
  }

  const trip = tripQ.data;
  const stops = trip.stops;

  const moveStop = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= stops.length) return;
    const order = stops.map((s) => s.id);
    const a = order[index];
    const b = order[target];
    if (a === undefined || b === undefined) return;
    order[index] = b;
    order[target] = a;
    void act(() => reorderStops(id, order), "Не удалось изменить порядок");
  };

  const onDeleteTrip = async () => {
    if (!confirm(`Удалить рейс №${trip.display_number ?? ""}?`)) return;
    try {
      await deleteTrip(id);
      qc.invalidateQueries({ queryKey: ["trips"] });
      navigate("/logistics");
    } catch (e) {
      toast.error(extractErrorMessage(e, "Не удалось удалить рейс"));
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5 pb-16">
      <div className="flex items-center justify-between">
        <BackLink onClick={() => navigate("/logistics")} />
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive"
          onClick={onDeleteTrip}
        >
          <Trash2 className="size-4" /> Удалить рейс
        </Button>
      </div>

      <h1 className="text-2xl font-semibold">Рейс №{trip.display_number ?? "—"}</h1>

      <HeaderCard
        trip={trip}
        onSave={(patch) => act(() => updateTrip(id, patch), "Не удалось сохранить")}
      />

      {/* Карта маршрута — сворачиваемо, скрипт Яндекса грузится только при раскрытии */}
      <div className="rounded-lg border bg-card p-4">
        <button
          type="button"
          onClick={() => setMapOpen((o) => !o)}
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {mapOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          Карта маршрута
        </button>
        {mapOpen && (
          <div className="mt-3">
            <TripMap tripId={id} stops={stops} />
          </div>
        )}
      </div>

      {/* Итог по рейсу (логистика) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
        <span className="font-semibold">Итого по рейсу:</span>
        <span className="text-muted-foreground">
          доставка <b className="text-foreground">{ruRub.format(trip.total_delivery)} ₽</b>
        </span>
        <span className="text-muted-foreground">
          + ожидание <b className="text-foreground">{ruRub.format(trip.total_waiting)} ₽</b>
        </span>
        <span className="text-muted-foreground">
          + возвраты <b className="text-foreground">{ruRub.format(trip.total_returns)} ₽</b>
        </span>
        <span className="ml-auto text-base font-semibold">
          = {ruRub.format(trip.total_cost)} ₽
        </span>
      </div>

      <div className="flex items-center justify-between pt-2">
        <h2 className="text-xl font-semibold">Точки маршрута</h2>
        <Button
          variant="outline"
          onClick={() => act(() => addStop(id, {}), "Не удалось добавить точку")}
        >
          <Plus className="size-4" /> Добавить точку
        </Button>
      </div>

      {stops.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Точек нет. Добавьте точку, укажите тип/организацию и привяжите заказы.
        </p>
      )}

      <div className="space-y-3">
        {stops.map((stop, i) => (
          <React.Fragment key={stop.id}>
            {i > 0 && (
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <span className="h-4 w-px bg-border" />
                доставка до точки {i + 1}:{" "}
                <span className="font-medium text-foreground">
                  {stop.delivery_cost != null ? `${ruRub.format(stop.delivery_cost)} ₽` : "—"}
                </span>
                {legs && legs[i - 1] != null && (
                  <span className="tabular-nums">· ~{legs[i - 1]} км</span>
                )}
              </div>
            )}
            <StopCard
            tripId={id}
            tripDate={trip.planned_date}
            stop={stop}
            index={i}
            total={stops.length}
            onMove={moveStop}
            onSaveStop={(patch) =>
              act(() => updateStop(id, stop.id, patch), "Не удалось сохранить точку")
            }
            onDeleteStop={() =>
              act(() => deleteStop(id, stop.id), "Не удалось удалить точку")
            }
            onAttach={(orderType, orderRef) =>
              act(
                () => attachOrder(id, stop.id, orderType, orderRef),
                "Не удалось привязать заказ",
              )
            }
            onDetach={(orderId) =>
              act(() => detachOrder(id, stop.id, orderId), "Не удалось отвязать")
            }
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" /> К списку рейсов
    </button>
  );
}

// ── Шапка рейса ───────────────────────────────────────────────────────────

interface HeaderForm {
  carrier_ref: string;
  carrier_name: string;
  driver_name: string;
  driver_phone: string;
  vehicle_plate: string;
  vehicle_model: string;
  vehicle_type: string;
  planned_date: string;
  comment: string;
  status: TripStatus;
}

function formFromTrip(t: TripDetail): HeaderForm {
  return {
    carrier_ref: t.carrier_kontragent_ref ?? "",
    carrier_name: t.carrier_kontragent_name ?? "",
    driver_name: t.driver_name ?? "",
    driver_phone: t.driver_phone ?? "",
    vehicle_plate: t.vehicle_plate ?? "",
    vehicle_model: t.vehicle_model ?? "",
    vehicle_type: t.vehicle_type ?? "",
    planned_date: t.planned_date ?? "",
    comment: t.comment ?? "",
    status: t.status,
  };
}

function HeaderCard({
  trip,
  onSave,
}: {
  trip: TripDetail;
  onSave: (patch: TripUpdateIn) => Promise<boolean>;
}): React.ReactElement {
  const baseline = useMemo(() => formFromTrip(trip), [trip]);
  const [form, setForm] = useState<HeaderForm>(baseline);
  const [saving, setSaving] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  // Ресинк формы после сохранения (пришёл свежий trip → новый baseline).
  useEffect(() => setForm(baseline), [baseline]);

  const set = <K extends keyof HeaderForm>(key: K, value: HeaderForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const suggestQ = useQuery({
    queryKey: ["driver-suggest", form.driver_name],
    queryFn: () => driverSuggest(form.driver_name),
    enabled: suggestOpen,
    staleTime: 30_000,
  });

  const pickSuggestion = (s: DriverSuggestion) => {
    setForm((f) => ({
      ...f,
      driver_name: s.driver_name,
      driver_phone: s.driver_phone,
      vehicle_plate: s.vehicle_plate,
      vehicle_model: s.vehicle_model,
    }));
    setSuggestOpen(false);
  };

  const save = async () => {
    setSaving(true);
    const ok = await onSave({
      carrier_kontragent_ref: form.carrier_ref || null,
      carrier_kontragent_name: form.carrier_name || null,
      driver_name: form.driver_name || null,
      driver_phone: form.driver_phone || null,
      vehicle_plate: form.vehicle_plate || null,
      vehicle_model: form.vehicle_model || null,
      vehicle_type: form.vehicle_type || null,
      planned_date: form.planned_date || null,
      comment: form.comment || null,
      status: form.status,
    });
    setSaving(false);
    if (ok) toast.success("Рейс сохранён");
  };

  return (
    <Card className="space-y-4 p-5">
      {/* Строка 1: перевозчик + дата + статус (выпадающий список) */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[16rem] max-w-sm flex-1 space-y-1.5">
          <Label>Перевозчик (контрагент 1С)</Label>
          <RemotePicker
            value={form.carrier_ref ? { ref: form.carrier_ref, name: form.carrier_name } : null}
            onChange={(row) => {
              set("carrier_ref", row?.ref ?? "");
              set("carrier_name", row?.name ?? "");
            }}
            search={listCarriers}
            queryKey={["logistics-carriers"]}
            placeholder="Найти перевозчика…"
          />
        </div>
        <div className="w-40 space-y-1.5">
          <Label htmlFor="date">Дата рейса</Label>
          <Input
            id="date"
            type="date"
            value={form.planned_date}
            onChange={(e) => set("planned_date", e.target.value)}
          />
        </div>
        <div className="w-44 space-y-1.5">
          <Label htmlFor="status">Статус</Label>
          <select
            id="status"
            value={form.status}
            onChange={(e) => set("status", e.target.value as TripStatus)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Строка 2: водитель + машина — компактный ряд */}
      <div className="flex flex-wrap gap-x-4 gap-y-3">
        <div className="relative w-56 space-y-1.5">
          <Label htmlFor="driver">Водитель (ФИО)</Label>
          <Input
            id="driver"
            value={form.driver_name}
            onChange={(e) => {
              set("driver_name", e.target.value);
              setSuggestOpen(true);
            }}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
            placeholder="Иванов Иван"
          />
          {suggestOpen && (suggestQ.data?.length ?? 0) > 0 && (
            <ul className="absolute z-30 mt-1 max-h-60 w-72 overflow-auto rounded-md border bg-card shadow-md">
              {suggestQ.data?.map((s, idx) => (
                <li key={`${s.driver_name}-${idx}`}>
                  <button
                    type="button"
                    className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickSuggestion(s)}
                  >
                    <span className="font-medium">{s.driver_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {[s.vehicle_plate, s.vehicle_model, s.driver_phone]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="w-44 space-y-1.5">
          <Label htmlFor="phone">Телефон</Label>
          <Input
            id="phone"
            value={form.driver_phone}
            onChange={(e) => set("driver_phone", e.target.value)}
            inputMode="tel"
            placeholder="+7…"
          />
        </div>

        <div className="w-36 space-y-1.5">
          <Label htmlFor="plate">Гос. номер</Label>
          <Input
            id="plate"
            value={form.vehicle_plate}
            onChange={(e) => set("vehicle_plate", e.target.value)}
            placeholder="А123ВС77"
          />
        </div>

        <div className="w-48 space-y-1.5">
          <Label htmlFor="model">Марка / модель</Label>
          <Input
            id="model"
            value={form.vehicle_model}
            onChange={(e) => set("vehicle_model", e.target.value)}
            placeholder="ГАЗель"
          />
        </div>

        <div className="w-40 space-y-1.5">
          <Label htmlFor="vtype">Тип машины</Label>
          <select
            id="vtype"
            value={form.vehicle_type}
            onChange={(e) => set("vehicle_type", e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <option value="">—</option>
            {VEHICLE_TYPES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Строка 3: комментарий на всю ширину */}
      <div className="space-y-1.5">
        <Label htmlFor="comment">Комментарий к рейсу</Label>
        <textarea
          id="comment"
          value={form.comment}
          onChange={(e) => set("comment", e.target.value)}
          rows={2}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder="Заметки по рейсу…"
        />
      </div>

      <div className="flex items-center justify-end gap-2 border-t pt-4">
        {dirty && (
          <Button variant="ghost" onClick={() => setForm(baseline)} disabled={saving}>
            Отменить
          </Button>
        )}
        <Button onClick={save} disabled={!dirty || saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Сохранить
        </Button>
      </div>
    </Card>
  );
}
